import {
  RecommendationType,
  type Recommendation,
  type RecommendationWithDetails,
  type RecommendationVisual,
} from '@centric-os/types/api/recommendations';
import { defineStore, type StateTree } from 'pinia';
import { isEmpty } from 'lodash-es';

interface State extends StateTree {
  recommendations: RecommendationWithDetails[];
  currentRecommendationIndex: number;
  hasError: boolean;
}

export function useRecommendationsStore(storeId = 'recommendations') {
  const store = defineStore(storeId, {
    state: (): State => ({
      recommendations: [],
      currentRecommendationIndex: 0,
      hasError: false,
    }),

    getters: {
      currentRecommendation: (state): RecommendationWithDetails | null =>
        state.recommendations[state.currentRecommendationIndex] || null,

      currentRecommendationType: (state): RecommendationType | null => {
        const rec = state.recommendations[state.currentRecommendationIndex];
        if (!rec) return null;

        if (rec.recommendationType) return rec.recommendationType;

        const recType = rec.rec_type?.toLowerCase();
        if (recType?.startsWith('cpg') || recType?.includes('cpg')) return RecommendationType.CPG;
        if (recType?.startsWith('expand')) return RecommendationType.EXPAND;
        if (recType?.startsWith('reduce')) return RecommendationType.REDUCE;

        return null;
      },

      currentTimeframeSubfilter: (state): string =>
        state.recommendations[state.currentRecommendationIndex]?.timeframe_subfilter || '',

      hasTableData: (state): boolean =>
        !!state.recommendations[state.currentRecommendationIndex]?.table?.length,
    },

    actions: {
      async getRecommendations(siteId: string): Promise<void> {
        try {
          this.hasError = false;

          const endpoint = `centricos/recommendations/${siteId}`;
          const { data } = await this.cdlApi.get<Recommendation[]>(endpoint);

          const validRecommendations = data.filter((rec) => {
            const visual = rec.visual;
            return visual && !isEmpty(visual);
          });

          this.recommendations = validRecommendations;
          this.currentRecommendationIndex = 0;
        } catch (err) {
          this.hasError = true;
          this.recommendations = [];
          throw err;
        }
      },

      nextRecommendation(): void {
        const nextIndex = (this.currentRecommendationIndex + 1) % this.recommendations.length;
        this.currentRecommendationIndex = nextIndex;
      },

      previousRecommendation(): void {
        const prevIndex =
          this.currentRecommendationIndex === 0
            ? this.recommendations.length - 1
            : this.currentRecommendationIndex - 1;
        this.currentRecommendationIndex = prevIndex;
      },
    },
  });

  return store();
}
